import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X, Search } from "lucide-react";
import { cn } from "@/lib/cn";

export interface ClientComboboxOption {
  id: string;
  name: string;
  status?: string;
}

/** Lowercase + strip accents for fuzzy matching. "déco" → "deco". */
export function normalizeSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

interface Props {
  options: ClientComboboxOption[];
  /** Selected client id, or "" for no client. */
  value: string;
  onChange: (id: string, name: string) => void;
  /** If true, shows a "No client" option at the top and a clear ×. Default true. */
  nullable?: boolean;
  disabled?: boolean;
}

export function ClientCombobox({ options, value, onChange, nullable = true, disabled = false }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.id === value) ?? null;

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = normalizeSearch(search);
    return options.filter((o) => normalizeSearch(o.name).includes(q));
  }, [options, search]);

  const noClientItem: ClientComboboxOption = useMemo(
    () => ({ id: "", name: t("clientCombobox.noClient") }),
    [t],
  );

  const allItems: ClientComboboxOption[] = useMemo(
    () => (nullable ? [noClientItem, ...filtered] : filtered),
    [nullable, noClientItem, filtered],
  );

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
    setCursor(-1);
  }, []);

  const select = useCallback(
    (id: string, name: string) => {
      onChange(id, name);
      close();
    },
    [onChange, close],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (cursor >= 0 && listRef.current) {
      const el = listRef.current.children[cursor] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [cursor]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, allItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = cursor >= 0 ? allItems[cursor] : undefined;
      if (item) select(item.id, item.name);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full" onKeyDown={handleKeyDown}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((prev) => !prev); }}
        className={cn(
          "w-full h-9 rounded-sm border border-border2 bg-bg4 px-3 text-[13px] outline-none transition-all flex items-center justify-between gap-2",
          "hover:border-primary focus:border-primary focus:bg-bg3",
          "disabled:cursor-not-allowed disabled:opacity-55",
          selected ? "text-text" : "text-text3",
        )}
      >
        <span className="truncate">{selected ? selected.name : t("clientCombobox.selectPlaceholder")}</span>
        <div className="flex items-center gap-1 shrink-0">
          {selected && nullable && (
            <span
              role="button"
              aria-label={t("clientCombobox.clearSelection")}
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange("", ""); }}
              className="p-0.5 rounded text-text3 hover:text-text hover:bg-bg2"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className={cn("h-3.5 w-3.5 text-text3 transition-transform duration-150", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="absolute z-[200] w-full mt-1 bg-card border border-border2 rounded-sm shadow-card overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 h-8 rounded-sm border border-border2 bg-bg4 px-2">
              <Search className="h-3.5 w-3.5 text-text3 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCursor(-1); }}
                placeholder={t("clientCombobox.typeToSearch")}
                className="flex-1 min-w-0 bg-transparent text-[13px] text-text outline-none placeholder:text-text3"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setCursor(-1); inputRef.current?.focus(); }}
                  className="text-text3 hover:text-text"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <ul ref={listRef} role="listbox" className="max-h-48 overflow-y-auto py-1 scroll-thin">
            {allItems.length === 0 ? (
              <li className="px-3 py-2.5 text-[12px] text-text3 text-center select-none">
                {t("clientCombobox.noResults")}
              </li>
            ) : (
              allItems.map((item, idx) => (
                <li
                  key={item.id === "" ? "__no_client__" : item.id}
                  role="option"
                  aria-selected={item.id === value}
                  onMouseEnter={() => setCursor(idx)}
                  onClick={() => select(item.id, item.name)}
                  className={cn(
                    "px-3 py-2 text-[13px] cursor-pointer select-none flex items-center justify-between gap-2",
                    idx === cursor ? "bg-bg2 text-text" : "text-text2 hover:bg-bg2 hover:text-text",
                    item.id === "" && "italic text-text3",
                    item.id !== "" && item.id === value && "text-primary font-medium",
                  )}
                >
                  <span className="truncate">{item.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.status && (item.status === "PAUSED" || item.status === "CHURNED") && (
                      <span className="text-[10px] text-warning">●</span>
                    )}
                    {item.id !== "" && item.id === value && (
                      <span className="text-primary text-[10px]">✓</span>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
